import type { OperatorTenant, SupportGrantStatus } from "@esi/shared";
import { useTranslation } from "react-i18next";
import { formatNumber, formatUsd } from "@/components/charts";
import { cn } from "@/lib/utils";
import { ApiError } from "../api-errors.ts";
import { t } from "../i18n/i18n.ts";

const STATUS_DOT: Record<OperatorTenant["status"], string> = {
  active: "bg-success-fg",
  suspended: "bg-warning-fg",
  deletion_scheduled: "bg-danger-fg",
  deleted: "bg-fg-muted",
};

/** A tenant's status: a dot and a word (never colour alone). */
export function TenantStatus({ status }: { status: OperatorTenant["status"] }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 text-fg-secondary">
      <span aria-hidden="true" className={cn("size-1.5 rounded-full", STATUS_DOT[status])} />
      {t(`console.tenantStatus.${status}`)}
    </span>
  );
}

export function GrantStatus({ status }: { status: SupportGrantStatus }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 text-fg-secondary">
      <span
        aria-hidden="true"
        className={cn(
          "size-1.5 rounded-full",
          status === "active"
            ? "bg-success-fg"
            : status === "requested"
              ? "bg-warning-fg"
              : "bg-fg-muted",
        )}
      />
      {t(`supportAccess.status.${status}`)}
    </span>
  );
}

/** This month's AI spend against the limit, e.g. "$212.40 / $200.00". */
export function spendText(spend: OperatorTenant["modelSpend"]): string {
  if (!spend) {
    // An older API revision (canary) does not send it.
    return "—";
  }
  return `${formatUsd(spend.usedThisMonth)} / ${limitText(spend.monthlyUsd, formatUsd)}`;
}

/**
 * The spend alert of a tenant (spec `tenant-plans`, "Spend alerts"): a word next to an icon-free
 * dot, so tone is never colour alone; nothing below 80 %.
 */
export function SpendAlert({ spend }: { spend: OperatorTenant["modelSpend"] }) {
  const { t } = useTranslation();
  if (!spend?.alert) {
    return null;
  }
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-xs font-medium",
        spend.alert === "reached" ? "text-danger-fg" : "text-warning-fg",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-1.5 rounded-full",
          spend.alert === "reached" ? "bg-danger-fg" : "bg-warning-fg",
        )}
      />
      {t(`console.spendAlert.${spend.alert}`)}
    </span>
  );
}

/** A limit, or "Unlimited". */
export function limitText(value: number | null, format: (n: number) => string = formatNumber) {
  return value === null ? t("plan.unlimited") : format(value);
}

/** A refused console request in plain words. */
export function consoleRefusal(error: Error): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "plan_in_use":
        return t("console.plans.inUse", {
          count: typeof error.details?.tenants === "number" ? error.details.tenants : 0,
        });
      case "seat_limit":
        return t("console.refused.seatLimit");
    }
    if (error.status === 422 || error.status === 409) {
      return error.explained ? error.message : t("errors.refused");
    }
    if (error.status === 403) {
      return t("errors.forbidden");
    }
  }
  return error.message;
}

/**
 * Use against a limit as a 4px meter under its figure (proposal ui-refresh-auth-console): neutral
 * below the spend alert's 80 %, warning from 80 % and danger at the limit, always beside the
 * figure in words, never colour alone. Nothing for an unlimited figure; a 0 limit is reached. `role="meter"`: a native
 * `<meter>` cannot take the token colours in every engine.
 */
export function UsageMeter(props: {
  used: number;
  limit: number | null;
  label: string;
  /** The tone when the server already decided it (the spend alert); else from the ratio. */
  level?: MeterLevel;
  className?: string;
}) {
  const { t } = useTranslation();
  if (props.limit === null) {
    return null;
  }
  // A limit of 0 is a real one (nothing allowed): it reads as reached, never as unlimited.
  const ratio = props.limit > 0 ? Math.max(0, props.used / props.limit) : 1;
  const level = props.level ?? meterLevel(ratio);
  return (
    // biome-ignore lint/a11y/useSemanticElements: a native <meter> cannot take the token colours in every engine
    <span
      role="meter"
      aria-label={props.label}
      aria-valuemin={0}
      aria-valuemax={props.limit}
      aria-valuenow={Math.min(Math.max(props.used, 0), props.limit)}
      aria-valuetext={t("console.tenantList.used", { percent: Math.round(ratio * 100) })}
      data-level={level}
      className={cn(
        // The hairline keeps the track visible on a raised surface in both themes.
        "mt-1.5 block h-1 w-full overflow-hidden rounded-full bg-surface-sunken ring-1 ring-line ring-inset",
        props.className,
      )}
    >
      <span
        className={cn("block h-full rounded-full", METER_FILL[level])}
        // A sliver stays visible for any use above zero.
        style={{ width: `${Math.min(100, ratio > 0 ? Math.max(ratio * 100, 2) : 0)}%` }}
      />
    </span>
  );
}

const METER_FILL = {
  normal: "bg-fg-muted",
  warning: "bg-warning-fg",
  reached: "bg-danger-fg",
} as const;

type MeterLevel = keyof typeof METER_FILL;

/** The spend alert's thresholds: warning from 80 %, reached at the limit. */
export function meterLevel(ratio: number): MeterLevel {
  return ratio >= 1 ? "reached" : ratio >= 0.8 ? "warning" : "normal";
}

/**
 * The spend meter's tone is the server's spend alert: its 80 % warning counts synthetic demo
 * spend, but "reached" counts live spend only (spec `tenant-plans`), so the ratio of the
 * displayed total alone would turn the meter red beside the words "80 % of spend limit".
 */
export function spendLevel(spend: NonNullable<OperatorTenant["modelSpend"]>): MeterLevel {
  return spend.alert ?? "normal";
}
